import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, UserCheck, UserPlus, X, Sparkles, Bell, Archive, Trash2, Eye, FileEdit, ChevronDown, ChevronUp, Mail, Phone, Target, LayoutTemplate, Wand2, Activity, MapPin, Heart, TrendingUp, Calendar as CalendarIcon, Clock, MessageCircle, DollarSign } from "lucide-react";
import AthleteProgramsPanel from "@/components/admin/AthleteProgramsPanel";

const SESSION_TYPES = ["easy","threshold","tempo","vo2","long","recovery","hill","strength","mobility","race"];

export default function AdminAthletes() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [programs, setPrograms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState(null); // athlete id whose profile is shown
  const [detail, setDetail] = useState(null);
  const [assignAthlete, setAssignAthlete] = useState(null);
  const [pickedProgramId, setPickedProgramId] = useState("");
  const [pickedTemplateId, setPickedTemplateId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignMode, setAssignMode] = useState("program"); // "program" | "template"

  // Inline AI draft modal state
  const [aiFor, setAiFor] = useState(null); // athlete summary obj (u + detail)
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDraft, setAiDraft] = useState(null); // generated program once AI returns
  const [aiForm, setAiForm] = useState({
    goal: "5k", weeks: 8, weekly_availability_days: 4, weekly_mileage_target_km: "",
    required_session_types: [], pace_zones: "", coach_guidance: "", auto_publish_and_assign: true,
  });

  const reload = () => api.get("/admin/athletes").then(r => setList(r.data));
  useEffect(() => {
    reload();
    api.get("/admin/programs").then(r => setPrograms(r.data));
    api.get("/admin/templates").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const filtered = list.filter(u => {
    if (q && !(u.email?.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase()))) return false;
    if (filter === "unassigned" && u.has_assignment) return false;
    if (filter === "assigned" && !u.has_assignment) return false;
    if (filter === "incomplete" && u.profile_complete) return false;
    return true;
  });

  const expand = async (u) => {
    if (expanded === u.id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(u.id);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/athletes/${u.id}`);
      setDetail(data);
    } catch {
      toast.error("Could not load profile");
    }
  };

  const archive = async (u) => {
    if (!window.confirm(`Archive ${u.name}? They will no longer be able to log in but their data is preserved.`)) return;
    await api.post(`/admin/athletes/${u.id}/archive`);
    toast.success(`${u.name} archived`);
    reload();
  };

  const remove = async (u) => {
    const firstName = u.name.split(" ")[0];
    const confirm1 = window.prompt(`DELETE ${u.name} permanently? This wipes their profile, programs, payments, recovery logs — everything.\n\nType "${firstName}" (or full name, or email) to confirm:`);
    if (!confirm1) return;
    const v = confirm1.toLowerCase().trim();
    const valid = (
      v === firstName.toLowerCase() ||
      v === u.name.toLowerCase().trim() ||
      v === (u.email || "").toLowerCase().trim()
    );
    if (!valid) {
      toast.error(`Did not match — expected "${firstName}", "${u.name}", or "${u.email}"`);
      return;
    }
    await api.delete(`/admin/athletes/${u.id}`);
    toast.success(`${u.name} deleted permanently`);
    setExpanded(null);
    reload();
  };

  const nudge = async (u) => {
    try {
      await api.post(`/admin/athletes/${u.id}/nudge`);
      toast.success(`Nudge sent to ${u.name.split(" ")[0]}`);
    } catch { toast.error("Could not send nudge"); }
  };

  const openAssign = (u) => {
    setAssignAthlete(u);
    const publishedMatch = programs.find(p => p.published && p.goal === u.goal);
    const firstPublished = programs.find(p => p.published);
    setPickedProgramId(publishedMatch?.id || firstPublished?.id || "");
    const tplMatch = templates.find(t => t.goal === u.goal);
    setPickedTemplateId(tplMatch?.id || templates[0]?.id || "");
    setAssignMode("program");
  };

  const doAssign = async () => {
    if (!assignAthlete) return;
    setAssignBusy(true);
    try {
      if (assignMode === "template") {
        if (!pickedTemplateId) { setAssignBusy(false); return; }
        const { data: cloned } = await api.post(`/admin/templates/${pickedTemplateId}/clone`, { athlete_id: assignAthlete.id, publish: true });
        const { data } = await api.post(`/admin/programs/${cloned.id}/assign`, { athlete_id: assignAthlete.id });
        toast.success(`${data.program} assigned to ${data.athlete}`);
      } else {
        if (!pickedProgramId) { setAssignBusy(false); return; }
        const { data } = await api.post(`/admin/programs/${pickedProgramId}/assign`, { athlete_id: assignAthlete.id });
        toast.success(`${data.program} assigned to ${data.athlete}`);
      }
      setAssignAthlete(null);
      reload();
      if (expanded === assignAthlete.id) {
        const { data: refreshed } = await api.get(`/admin/athletes/${assignAthlete.id}`);
        setDetail(refreshed);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    }
    setAssignBusy(false);
  };

  const goToProgram = (programId) => {
    // Navigate to /admin/programs with anchor — quickest UX
    window.location.assign(`/admin/programs#${programId}`);
  };

  // ===== Inline AI Draft =====
  const openAiDraft = (u, det) => {
    const profile = det?.user || {};
    setAiFor({ ...u, ...profile });
    setAiDraft(null);
    setAiForm({
      goal: profile.goal_race || profile.goal || u.goal || "5k",
      weeks: profile.goal_race === "marathon" ? 16 : (profile.goal_race === "half" ? 12 : 8),
      weekly_availability_days: profile.weekly_availability_days || 4,
      weekly_mileage_target_km: profile.weekly_mileage_km || "",
      required_session_types: [],
      pace_zones: "",
      coach_guidance: "",
      auto_publish_and_assign: true,
    });
  };

  const runAiDraft = async () => {
    if (!aiFor) return;
    setAiBusy(true);
    setAiDraft(null);
    try {
      const payload = {
        ...aiForm,
        athlete_id: aiFor.id,
      };
      if (payload.weekly_mileage_target_km === "" || payload.weekly_mileage_target_km == null) delete payload.weekly_mileage_target_km;
      else payload.weekly_mileage_target_km = Number(payload.weekly_mileage_target_km);
      if (!payload.required_session_types?.length) delete payload.required_session_types;
      if (!payload.pace_zones) delete payload.pace_zones;
      if (!payload.coach_guidance) delete payload.coach_guidance;
      const { data } = await api.post("/admin/ai/generate-program", payload);
      if (data.auto_assigned_to) {
        toast.success(`Program generated, published & sent to ${data.auto_assigned_to} ✅`);
        setAiFor(null);
        reload();
        if (expanded === aiFor.id) {
          const { data: refreshed } = await api.get(`/admin/athletes/${aiFor.id}`);
          setDetail(refreshed);
        }
      } else {
        setAiDraft(data);
        toast.success(`Draft ready (${data.sessions?.length || 0} sessions) — review below, then publish.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    }
    setAiBusy(false);
  };

  const publishAndAssignDraft = async () => {
    if (!aiDraft || !aiFor) return;
    setAiBusy(true);
    try {
      await api.patch(`/admin/programs/${aiDraft.id}`, { published: true, status: "approved" });
      const { data } = await api.post(`/admin/programs/${aiDraft.id}/assign`, { athlete_id: aiFor.id });
      toast.success(`${data.program} sent to ${data.athlete} ✅`);
      setAiFor(null);
      setAiDraft(null);
      reload();
      if (expanded === aiFor.id) {
        const { data: refreshed } = await api.get(`/admin/athletes/${aiFor.id}`);
        setDetail(refreshed);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish/assign failed");
    }
    setAiBusy(false);
  };

  const editDraftInBuilder = () => {
    if (!aiDraft) return;
    window.location.assign(`/admin/programs#${aiDraft.id}`);
  };

  const unassignedCount = list.filter(u => !u.has_assignment).length;
  const assignedCount = list.length - unassignedCount;
  const incompleteCount = list.filter(u => !u.profile_complete).length;
  const publishedPrograms = programs.filter(p => p.published);

  return (
    <div data-testid="admin-athletes">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">ATHLETES</div>
      <h1 className="font-display text-4xl uppercase">Manage <span className="text-[#d7ff00]">runners</span></h1>

      <div className="mt-6 flex flex-wrap gap-2 items-center">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">All · {list.length}</FilterChip>
        <FilterChip active={filter === "unassigned"} onClick={() => setFilter("unassigned")} testid="filter-unassigned" warning>
          <AlertCircle className="w-3 h-3" /> Unassigned · {unassignedCount}
        </FilterChip>
        <FilterChip active={filter === "assigned"} onClick={() => setFilter("assigned")} testid="filter-assigned">
          <UserCheck className="w-3 h-3" /> On a plan · {assignedCount}
        </FilterChip>
        <FilterChip active={filter === "incomplete"} onClick={() => setFilter("incomplete")} testid="filter-incomplete" warning>
          <Bell className="w-3 h-3" /> Profile incomplete · {incompleteCount}
        </FilterChip>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email…"
          className="ml-auto flex-1 sm:flex-initial sm:w-72 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-2" data-testid="athlete-search" />
      </div>

      <div className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
        {filtered.length === 0 && <div className="p-5 text-[#888] text-sm">No athletes match this filter.</div>}
        {filtered.map(u => (
          <div key={u.id} data-testid={`athlete-${u.id}`}>
            <div className="p-4 flex items-center justify-between flex-wrap gap-3">
              <button onClick={() => expand(u)} className="flex items-center gap-3 min-w-0 text-left hover:opacity-80" data-testid={`expand-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#1f1f1f] flex items-center justify-center font-display flex-shrink-0">{(u.name?.split(" ").map(n => n[0]).join("")||"").slice(0,2).toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="font-display text-lg truncate flex items-center gap-2">{u.name} {expanded === u.id ? <ChevronUp className="w-3 h-3 text-[#d7ff00]" /> : <ChevronDown className="w-3 h-3 text-[#666]" />}</div>
                  <div className="font-mono-jp text-[10px] text-[#888] uppercase tracking-widest truncate">{u.email}</div>
                </div>
              </button>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="text-right hidden sm:block">
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{u.country || "—"} · {u.goal?.toUpperCase() || "NO GOAL"}</div>
                  <div className={`font-mono-jp text-[10px] uppercase tracking-widest ${u.has_active_subscription ? "text-[#d7ff00]" : "text-[#666]"}`}>{u.has_active_subscription ? "ACTIVE" : "FREE"}</div>
                </div>
                {u.has_coaching && (
                  <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#0a0a0a] bg-gradient-to-r from-[#d7ff00] to-[#a8c800] px-2 py-0.5 flex items-center gap-1" data-testid={`coaching-badge-${u.id}`} title="Active coaching subscription">
                    <MessageCircle className="w-2.5 h-2.5" /> COACHING
                  </span>
                )}
                {!u.profile_complete && (
                  <button onClick={() => nudge(u)} title="Send onboarding nudge"
                    className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/60 px-2 py-0.5 hover:bg-[#d7ff00]/10 flex items-center gap-1" data-testid={`nudge-${u.id}`}>
                    <Bell className="w-2.5 h-2.5" /> NUDGE
                  </button>
                )}
                {u.has_assignment ? (
                  <button onClick={() => openAssign(u)} className="font-mono-jp text-[9px] uppercase tracking-widest text-[#070707] bg-[#d7ff00] px-2 py-0.5 flex items-center gap-1 hover:bg-white" data-testid={`reassign-${u.id}`}>
                    <UserCheck className="w-2.5 h-2.5" /> ON PLAN · CHANGE
                  </button>
                ) : (
                  <button onClick={() => openAssign(u)}
                    className="font-mono-jp text-[9px] uppercase tracking-widest text-[#ff9500] border border-[#ff9500]/60 px-2 py-0.5 hover:bg-[#ff9500]/10 flex items-center gap-1" data-testid={`assign-link-${u.id}`}>
                    <UserPlus className="w-2.5 h-2.5" /> ASSIGN
                  </button>
                )}
              </div>
            </div>

            {/* Expanded profile */}
            {expanded === u.id && (
              <div className="bg-[#070707] border-t border-[#1f1f1f] p-5" data-testid={`profile-expand-${u.id}`}>
                {!detail ? (
                  <div className="text-[#888] text-sm font-mono-jp">Loading profile…</div>
                ) : (
                  <div className="grid lg:grid-cols-[1fr_320px] gap-5">
                    <div>
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] mb-3">ATHLETE PROFILE</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        <P l="Phone" v={detail.user?.phone} icon={Phone} />
                        <P l="Age" v={detail.user?.age} />
                        <P l="Sex" v={detail.user?.sex} />
                        <P l="Weight" v={detail.user?.weight_kg ? `${detail.user.weight_kg} kg` : null} />
                        <P l="Height" v={detail.user?.height_cm ? `${detail.user.height_cm} cm` : null} />
                        <P l="Level" v={detail.user?.running_level} />
                        <P l="Weekly km" v={detail.user?.weekly_mileage_km ? `${detail.user.weekly_mileage_km} km` : null} />
                        <P l="Days/wk" v={detail.user?.weekly_availability_days} />
                        <P l="Goal race" v={detail.user?.goal_race?.toUpperCase()} />
                        <P l="Goal time" v={detail.user?.goal_time} icon={Target} />
                        <P l="Race date" v={detail.user?.target_race_date} />
                        <P l="5K PB" v={detail.user?.pb_5k} />
                        <P l="10K PB" v={detail.user?.pb_10k} />
                        <P l="Half PB" v={detail.user?.pb_half} />
                        <P l="Marathon PB" v={detail.user?.pb_marathon} />
                        <P l="Experience" v={detail.user?.running_experience_years ? ({under_1:"<1y","1_3":"1-3y","3_5":"3-5y","5_10":"5-10y",over_10:"10+y"})[detail.user.running_experience_years] : null} />
                        <P l="Surface" v={detail.user?.training_surface} />
                        <P l="Races 12mo" v={detail.user?.races_last_year} />
                        <P l="Cross-train" v={(detail.user?.cross_training || []).join(", ") || null} />
                      </div>
                      {detail.user?.injuries && (
                        <div className="mt-3 bg-[#1a0e0e] border border-[#ff9500]/40 p-3">
                          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#ff9500]">INJURY HISTORY</div>
                          <div className="text-sm text-white mt-1">{detail.user.injuries}</div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      {/* Coaching grant / revoke */}
                      <CoachingControl uid={u.id} firstName={u.name.split(" ")[0]} hasCoaching={detail.user?.has_coaching} startDate={detail.user?.coaching_start_date} endDate={detail.user?.coaching_end_date} onChange={async () => {
                        reload();
                        try {
                          const { data } = await api.get(`/admin/athletes/${u.id}`);
                          setDetail(data);
                        } catch (e) { console.error("Failed to reload athlete detail:", e); }
                      }} />

                      {/* Record payment made outside the app (bank transfer, cash) */}
                      <OfflinePaymentControl uid={u.id} firstName={u.name.split(" ")[0]} onChange={async () => {
                        reload();
                        try {
                          const { data } = await api.get(`/admin/athletes/${u.id}`);
                          setDetail(data);
                        } catch (e) { console.error("Failed to reload athlete detail:", e); }
                      }} />

                      {/* Payment history (HitPay + offline) */}
                      {detail.payments && detail.payments.length > 0 && (
                        <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`payments-history-${u.id}`}>
                          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center justify-between">
                            <span>PAYMENTS · {detail.payments.length}</span>
                            <span className="text-[#71c4ff]">Total {detail.payments[0]?.currency || "RM"} {detail.total_paid}</span>
                          </div>
                          <div className="mt-2 divide-y divide-[#1a1a1a] max-h-44 overflow-y-auto">
                            {detail.payments.map(p => (
                              <div key={p.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                                <div className="min-w-0">
                                  <div className="text-white truncate">{p.package_name}</div>
                                  <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666] mt-0.5">
                                    {(p.paid_at || p.created_at || "").slice(0, 10)} · {p.source === "offline" ? (p.payment_method || "offline") : "online"}
                                    {p.note ? ` · ${p.note}` : ""}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="font-display text-base">{p.currency} {p.amount}</div>
                                  <span className={`font-mono-jp text-[8px] uppercase tracking-widest px-1.5 py-0.5 ${p.status === "recorded" ? "bg-[#71c4ff]/15 text-[#71c4ff]" : "bg-[#d7ff00]/15 text-[#d7ff00]"}`}>
                                    {p.status === "recorded" ? "logged" : "active"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reset training program — fixes 'preparing your program' / empty dashboard */}
                      <ResetAssignmentControl uid={u.id} firstName={u.name.split(" ")[0]} onChange={async () => {
                        reload();
                        try {
                          const { data } = await api.get(`/admin/athletes/${u.id}`);
                          setDetail(data);
                        } catch (e) { console.error("Failed to reload athlete detail:", e); }
                      }} />

                      {/* Inline AI Draft CTA */}
                      <button
                        onClick={() => openAiDraft(u, detail)}
                        className="w-full bg-gradient-to-r from-[#d7ff00] to-[#a8c800] text-black font-display uppercase tracking-wider py-3 hover:opacity-90 flex items-center justify-center gap-2"
                        data-testid={`ai-draft-${u.id}`}
                      >
                        <Wand2 className="w-4 h-4" /> AI Draft for {u.name.split(" ")[0]}
                      </button>

                      {/* Drafts */}
                      {detail.drafts && detail.drafts.length > 0 && (
                        <div className="bg-[#0a0a0a] border border-[#d7ff00]/40 p-4">
                          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> AI DRAFTS · {detail.drafts.length}
                          </div>
                          <div className="mt-2 space-y-2">
                            {detail.drafts.map(d => (
                              <div key={d.id} className="bg-[#0e0e0e] border border-[#222] p-2.5">
                                <div className="font-display text-sm uppercase truncate">{d.name}</div>
                                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{d.sessions?.length || 0} sessions · {d.published ? <span className="text-[#d7ff00]">PUBLISHED</span> : <span className="text-[#ff9500]">DRAFT — needs review</span>}</div>
                                <div className="mt-2 flex gap-2">
                                  <button onClick={() => goToProgram(d.id)} className="text-[10px] font-mono-jp uppercase tracking-widest border border-[#d7ff00] text-[#d7ff00] px-2 py-1 hover:bg-[#d7ff00]/10 flex items-center gap-1" data-testid={`view-draft-${d.id}`}>
                                    <FileEdit className="w-3 h-3" /> Review & edit
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Current program */}
                      {detail.assigned_program && (
                        <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4">
                          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">CURRENT PROGRAM</div>
                          <div className="mt-1 font-display text-base uppercase">{detail.assigned_program.name}</div>
                          <div className="font-mono-jp text-[10px] text-[#888]">{detail.assigned_program.sessions?.length || 0} sessions · {detail.assigned_program.weeks}w</div>
                        </div>
                      )}

                      {/* Quick stats */}
                      <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4 grid grid-cols-2 gap-3">
                        <div><div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">LOGS</div><div className="font-display text-2xl text-[#d7ff00]">{(detail.session_logs?.length || 0) + (detail.manual_workouts?.length || 0)}</div><div className="text-[10px] text-[#666] font-mono-jp uppercase tracking-widest">sessions logged</div></div>
                        <div><div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">SUB</div><div className={`font-display text-xl ${detail.user?.has_active_subscription ? "text-[#d7ff00]" : "text-[#666]"}`}>{detail.user?.has_active_subscription ? "ACTIVE" : "FREE"}</div></div>
                      </div>

                      {/* Danger actions */}
                      <div className="bg-[#0a0a0a] border border-[#ff3b30]/30 p-4">
                        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#ff3b30]">ADMIN ACTIONS</div>
                        <div className="mt-3 flex flex-col gap-2">
                          <button onClick={() => archive(u)} className="flex items-center gap-2 border border-[#ff9500]/60 text-[#ff9500] hover:bg-[#ff9500]/10 font-mono-jp text-[10px] uppercase tracking-widest px-3 py-2" data-testid={`archive-${u.id}`}>
                            <Archive className="w-3.5 h-3.5" /> Archive athlete
                          </button>
                          <button onClick={() => remove(u)} className="flex items-center gap-2 border border-[#ff3b30]/60 text-[#ff3b30] hover:bg-[#ff3b30]/10 font-mono-jp text-[10px] uppercase tracking-widest px-3 py-2" data-testid={`delete-${u.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete permanently
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* === Training history, PBs & progress === */}
                {detail && (
                  <div className="mt-4">
                    <AthleteProgramsPanel uid={u.id} />
                  </div>
                )}

                {/* === Activity & Plan view === */}
                {detail && (
                  <AthleteActivityPanel detail={detail} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {assignAthlete && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !assignBusy && setAssignAthlete(null)} data-testid="athlete-assign-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-2"><UserPlus className="w-3 h-3" /> ASSIGN PLAN</div>
                <h3 className="font-display text-2xl uppercase">{assignAthlete.name}</h3>
                <p className="text-xs text-[#888] mt-1">Goal: {assignAthlete.goal?.toUpperCase() || "NOT SET"} · {assignAthlete.weekly_mileage_km ? `${assignAthlete.weekly_mileage_km} km/week` : "no mileage on file"}</p>
              </div>
              {!assignBusy && <button onClick={() => setAssignAthlete(null)} className="text-[#888]" data-testid="athlete-assign-close"><X /></button>}
            </div>

            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Source</label>
            <div className="mt-2 flex gap-1 mb-3 border border-[#222]">
              <button
                onClick={() => setAssignMode("program")}
                className={`flex-1 py-2 font-mono-jp text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${assignMode === "program" ? "bg-[#d7ff00] text-black" : "text-[#888] hover:text-white"}`}
                data-testid="assign-mode-program"
              >
                Published programs · {publishedPrograms.length}
              </button>
              <button
                onClick={() => setAssignMode("template")}
                className={`flex-1 py-2 font-mono-jp text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${assignMode === "template" ? "bg-[#d7ff00] text-black" : "text-[#888] hover:text-white"}`}
                data-testid="assign-mode-template"
              >
                <LayoutTemplate className="w-3 h-3" /> Templates · {templates.length}
              </button>
            </div>

            {assignMode === "program" ? (
              publishedPrograms.length === 0 ? (
                <div className="mt-2 p-3 border border-[#222] text-xs text-[#888]">
                  No published programs yet. Use <span className="text-[#d7ff00]">Programs → AI Generate</span> to create a draft, then publish it.
                </div>
              ) : (
                <select value={pickedProgramId} onChange={e => setPickedProgramId(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="athlete-assign-program">
                  {publishedPrograms.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.goal?.toUpperCase()}{p.target_athlete_id === assignAthlete.id ? " · MADE FOR THIS ATHLETE" : ""}
                    </option>
                  ))}
                </select>
              )
            ) : (
              templates.length === 0 ? (
                <div className="mt-2 p-3 border border-[#222] text-xs text-[#888]">
                  No saved templates yet. Open any program → click <span className="text-[#d7ff00]">Save as template</span> to reuse it.
                </div>
              ) : (
                <>
                  <select value={pickedTemplateId} onChange={e => setPickedTemplateId(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="athlete-assign-template">
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} · {t.goal?.toUpperCase()} · {t.weeks}w · {t.sessions?.length || 0} sessions
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-[10px] font-mono-jp text-[#666] uppercase tracking-widest">
                    Template is cloned into a new published program tailored to {assignAthlete.name.split(" ")[0]}.
                  </p>
                </>
              )
            )}

            <button
              onClick={doAssign}
              disabled={assignBusy || (assignMode === "program" ? !pickedProgramId : !pickedTemplateId)}
              className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="athlete-assign-confirm"
            >
              <UserPlus className="w-4 h-4" />
              {assignBusy ? "Assigning…" : `${assignMode === "template" ? "Clone & assign" : "Assign"} to ${assignAthlete.name.split(" ")[0]}`}
            </button>

            <a href="/admin/programs" className="mt-3 block text-center font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] hover:text-white" data-testid="goto-programs-link">
              → Go to Programs to AI-generate a tailored draft for {assignAthlete.name.split(" ")[0]}
            </a>
          </div>
        </div>
      )}

      {/* Inline AI Draft modal */}
      {aiFor && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => !aiBusy && setAiFor(null)} data-testid="ai-inline-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-3xl my-6 p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div className="min-w-0">
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1.5"><Wand2 className="w-3 h-3" /> AI DRAFT</div>
                <h3 className="font-display text-2xl uppercase truncate">{aiFor.name}</h3>
                <p className="text-[10px] font-mono-jp text-[#888] uppercase tracking-widest mt-0.5">
                  {(aiFor.running_level || "—")} · {aiFor.weekly_mileage_km ? `${aiFor.weekly_mileage_km} km/wk` : "no mileage"} · {aiFor.goal_race?.toUpperCase() || aiFor.goal?.toUpperCase() || "no goal"}{aiFor.goal_time ? ` · target ${aiFor.goal_time}` : ""}
                </p>
              </div>
              {!aiBusy && <button onClick={() => setAiFor(null)} className="text-[#888] flex-shrink-0" data-testid="ai-inline-close"><X /></button>}
            </div>

            {/* Athlete context strip */}
            <div className="bg-[#070707] border border-[#1f1f1f] p-3 mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <CtxStat label="5K PB" v={aiFor.pb_5k} />
              <CtxStat label="10K PB" v={aiFor.pb_10k} />
              <CtxStat label="Half PB" v={aiFor.pb_half} />
              <CtxStat label="Race date" v={aiFor.target_race_date} />
              {aiFor.injuries && (
                <div className="col-span-2 sm:col-span-4 bg-[#1a0e0e] border border-[#ff9500]/40 p-2 mt-1">
                  <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#ff9500]">INJURY HISTORY</div>
                  <div className="text-xs text-white mt-0.5">{aiFor.injuries}</div>
                </div>
              )}
            </div>

            {!aiDraft && (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldLbl label="Goal">
                    <select value={aiForm.goal} onChange={e => setAiForm({ ...aiForm, goal: e.target.value })} className={fld} data-testid="ai-inline-goal">
                      {["beginner","5k","10k","half","marathon"].map(g => <option key={g} value={g}>{g.toUpperCase()}</option>)}
                    </select>
                  </FieldLbl>
                  <FieldLbl label="Total weeks">
                    <select value={aiForm.weeks} onChange={e => setAiForm({ ...aiForm, weeks: Number(e.target.value) })} className={fld} data-testid="ai-inline-weeks">
                      {[4, 6, 8, 10, 12, 16].map(w => <option key={w} value={w}>{w} weeks</option>)}
                    </select>
                  </FieldLbl>
                  <FieldLbl label="Training days / week">
                    <input type="number" min={1} max={7} value={aiForm.weekly_availability_days} onChange={e => setAiForm({ ...aiForm, weekly_availability_days: Number(e.target.value) })} className={fld} data-testid="ai-inline-days" />
                  </FieldLbl>
                  <FieldLbl label="Target peak weekly mileage (km)">
                    <input type="number" value={aiForm.weekly_mileage_target_km} onChange={e => setAiForm({ ...aiForm, weekly_mileage_target_km: e.target.value })} placeholder="e.g. 60" className={fld} data-testid="ai-inline-mileage" />
                  </FieldLbl>
                  <FieldLbl label="Pace zones (optional)">
                    <input value={aiForm.pace_zones} onChange={e => setAiForm({ ...aiForm, pace_zones: e.target.value })} placeholder="Easy 5:30-6:00, Thresh 3:50-4:00" className={fld} data-testid="ai-inline-paces" />
                  </FieldLbl>
                </div>
                <FieldLbl label="Required session types">
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {SESSION_TYPES.map(t => {
                      const has = aiForm.required_session_types.includes(t);
                      return (
                        <button key={t} type="button" onClick={() => setAiForm(f => ({ ...f, required_session_types: has ? f.required_session_types.filter(x => x !== t) : [...f.required_session_types, t] }))}
                          className={`px-2 py-1 font-mono-jp text-[9px] uppercase tracking-widest border ${has ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb]"}`} data-testid={`ai-inline-type-${t}`}>{t}</button>
                      );
                    })}
                  </div>
                </FieldLbl>
                <FieldLbl label="Coach guidance for this athlete">
                  <textarea rows={3} value={aiForm.coach_guidance} onChange={e => setAiForm({ ...aiForm, coach_guidance: e.target.value })} placeholder={`e.g. Long runs Sunday only. Include strides. Build slowly given ${aiFor.injuries ? 'injury history.' : 'low base mileage.'}`} className={fld} data-testid="ai-inline-guidance" />
                </FieldLbl>

                <label className="mt-4 flex items-start gap-3 bg-[#1a1f00] border border-[#d7ff00] p-3 cursor-pointer">
                  <input type="checkbox" checked={aiForm.auto_publish_and_assign} onChange={e => setAiForm({ ...aiForm, auto_publish_and_assign: e.target.checked })} className="mt-0.5 accent-[#d7ff00]" data-testid="ai-inline-autosend" />
                  <div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">⚡ 1-CLICK SEND</div>
                    <div className="text-xs text-[#bbb] mt-1">Generate + publish + assign to {aiFor.name.split(" ")[0]} instantly. Uncheck to review the draft first.</div>
                  </div>
                </label>

                <button onClick={runAiDraft} disabled={aiBusy} className="mt-5 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-inline-generate">
                  <Sparkles className="w-4 h-4" /> {aiBusy ? "Claude is drafting… (10-30s)" : (aiForm.auto_publish_and_assign ? `Generate & send to ${aiFor.name.split(" ")[0]}` : "Generate draft to review")}
                </button>
              </>
            )}

            {aiDraft && (
              <div className="space-y-4" data-testid="ai-inline-draft-preview">
                <div className="bg-[#070707] border border-[#d7ff00]/40 p-4">
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">DRAFT READY</div>
                  <div className="font-display text-xl uppercase mt-1">{aiDraft.name}</div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mt-1">
                    {aiDraft.goal?.toUpperCase()} · {aiDraft.weeks}w · {aiDraft.sessions?.length || 0} sessions
                  </div>
                </div>

                {/* Week 1 preview */}
                <div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] mb-2">WEEK 1 PREVIEW</div>
                  <div className="bg-[#0e0e0e] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
                    {(aiDraft.sessions || []).filter(s => (s.week_index || 0) === 0).sort((a,b) => a.day_index - b.day_index).map(s => (
                      <div key={`${s.week_index}-${s.day_index}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] w-10">D{s.day_index + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="font-mono-jp text-[9px] text-[#888] uppercase tracking-widest">{s.type} {s.distance_km ? `· ${s.distance_km}km` : ""} {s.duration_min ? `· ${s.duration_min}min` : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button onClick={publishAndAssignDraft} disabled={aiBusy} className="flex-1 bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-inline-publish-assign">
                    <UserPlus className="w-4 h-4" /> {aiBusy ? "Sending…" : `Publish & send to ${aiFor.name.split(" ")[0]}`}
                  </button>
                  <button onClick={editDraftInBuilder} className="bg-[#0e0e0e] border border-[#d7ff00] text-[#d7ff00] font-mono-jp text-[10px] uppercase tracking-widest px-4 py-3 hover:bg-[#d7ff00]/10 flex items-center gap-2" data-testid="ai-inline-edit">
                    <FileEdit className="w-3.5 h-3.5" /> Edit in builder
                  </button>
                  <button onClick={() => setAiDraft(null)} className="bg-[#111] border border-[#222] text-[#bbb] font-mono-jp text-[10px] uppercase tracking-widest px-4 py-3 hover:border-[#d7ff00]" data-testid="ai-inline-regenerate">
                    Regenerate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const fld = "w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm";

function FieldLbl({ label, children }) {
  return (
    <div className="mt-3">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CtxStat({ label, v }) {
  return (
    <div>
      <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">{label}</div>
      <div className="text-white text-sm">{v || <span className="text-[#555]">—</span>}</div>
    </div>
  );
}

function P({ l, v, icon: Icon }) {
  return (
    <div>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {l}</div>
      <div className="text-white text-sm mt-0.5">{v || <span className="text-[#777]">—</span>}</div>
    </div>
  );
}

function FilterChip({ active, onClick, testid, children, warning }) {
  const base = "inline-flex items-center gap-1 px-3 py-2 text-xs font-mono-jp uppercase tracking-widest border";
  const cls = active
    ? (warning ? "border-[#ff9500] text-[#ff9500] bg-[#ff9500]/10" : "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10")
    : "border-[#222] text-[#bbb] hover:border-[#d7ff00] hover:text-white";
  return <button onClick={onClick} className={`${base} ${cls}`} data-testid={testid}>{children}</button>;
}


function AthleteActivityPanel({ detail }) {
  const [tab, setTab] = useState("program"); // program | sessions | recovery
  const [weekIdx, setWeekIdx] = useState(0);
  const prog = detail.assigned_program;
  const recovery = detail.recovery_logs || [];
  const sessionLogs = detail.session_logs || [];
  const manualWorkouts = detail.manual_workouts || [];

  const programWeeks = prog?.weeks || 0;
  const weekSessions = (prog?.sessions || []).filter(s => (s.week_index || 0) === weekIdx).sort((a, b) => a.day_index - b.day_index);

  const logsCount = sessionLogs.length + manualWorkouts.length;

  return (
    <div className="mt-6 border-t border-[#1f1f1f] pt-5" data-testid="athlete-activity-panel">
      <div className="flex gap-1 border border-[#1f1f1f] bg-[#070707] mb-4 flex-wrap">
        <PanelTab active={tab === "program"} onClick={() => setTab("program")} icon={CalendarIcon} count={programWeeks} testid="activity-tab-program">Program</PanelTab>
        <PanelTab active={tab === "sessions"} onClick={() => setTab("sessions")} icon={FileEdit} count={logsCount} testid="activity-tab-sessions">Logs &amp; Notes</PanelTab>
        <PanelTab active={tab === "recovery"} onClick={() => setTab("recovery")} icon={Heart} count={recovery.length} testid="activity-tab-recovery">Recovery</PanelTab>
      </div>

      {tab === "program" && (
        <div data-testid="program-tab-content">
          {!prog ? (
            <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5 text-center">
              <CalendarIcon className="w-6 h-6 text-[#444] mx-auto mb-2" />
              <div className="text-sm text-[#bbb]">No program assigned yet.</div>
              <div className="text-xs text-[#666] mt-1">Use the AI Draft button above to create one in 2 clicks.</div>
            </div>
          ) : (
            <>
              <div className="bg-[#070707] border border-[#d7ff00]/40 p-4 mb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">CURRENT PLAN</div>
                    <div className="font-display text-xl uppercase mt-0.5">{prog.name}</div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mt-0.5">{prog.goal?.toUpperCase()} · {prog.weeks} weeks · {prog.sessions?.length || 0} sessions</div>
                  </div>
                  <a href={`/admin/programs#${prog.id}`} className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00] px-3 py-1.5 hover:bg-[#d7ff00]/10 flex items-center gap-1" data-testid="program-tab-edit"><FileEdit className="w-3 h-3" /> Edit in builder</a>
                </div>
              </div>

              {/* Week selector */}
              <div className="flex items-center gap-1 mb-3 overflow-x-auto hide-scrollbar">
                {Array.from({ length: programWeeks }).map((_, i) => (
                  <button key={i} onClick={() => setWeekIdx(i)}
                    className={`flex-shrink-0 px-3 py-1.5 font-mono-jp text-[10px] uppercase tracking-widest border ${weekIdx === i ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb] hover:border-[#d7ff00]"}`}
                    data-testid={`program-week-${i}`}>
                    W{i + 1}
                  </button>
                ))}
              </div>

              {/* Week sessions */}
              <div className="bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
                {weekSessions.length === 0 && <div className="px-4 py-3 text-sm text-[#888]">No sessions in week {weekIdx + 1}.</div>}
                {weekSessions.map(s => (
                  <div key={`${s.week_index}-${s.day_index}`} className="px-4 py-3 flex items-start gap-3" data-testid={`prog-session-${s.week_index}-${s.day_index}`}>
                    <div className="flex-shrink-0 w-10 text-center">
                      <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">DAY</div>
                      <div className="font-display text-xl text-white">{s.day_index + 1}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-white">{s.name}</div>
                        <span className="font-mono-jp text-[9px] uppercase tracking-widest px-1.5 border border-[#d7ff00]/40 text-[#d7ff00]">{s.type}</span>
                      </div>
                      <div className="mt-1 text-xs text-[#bbb] flex items-center gap-3 flex-wrap">
                        {s.distance_km && <span className="text-[#d7ff00]">{s.distance_km} km</span>}
                        {s.duration_min && <span><Clock className="w-2.5 h-2.5 inline" /> {s.duration_min} min</span>}
                        {s.pace_target && <span className="text-[#bbb]">{s.pace_target}</span>}
                        {s.hr_zone && <span className="text-[#ff6b6b]">{s.hr_zone}</span>}
                      </div>
                      {s.description && <div className="mt-1 text-xs text-[#888] line-clamp-2">{s.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "sessions" && (
        <div data-testid="sessions-tab-content">
          {sessionLogs.length === 0 && manualWorkouts.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5 text-center">
              <FileEdit className="w-6 h-6 text-[#444] mx-auto mb-2" />
              <div className="text-sm text-[#bbb]">No completed sessions or manual workouts yet.</div>
              <div className="text-xs text-[#666] mt-1">Once the athlete marks sessions complete or logs ad-hoc workouts, you&apos;ll see their feedback, intervals, and notes here.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Completed planned sessions */}
              {sessionLogs.map(l => (
                <div key={l.id} className="bg-[#0a0a0a] border border-[#1f1f1f] border-l-4 border-l-[#d7ff00] p-4" data-testid={`session-log-${l.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">
                        PROGRAM SESSION · W{(l.week_index || 0) + 1} · DAY {(l.day_index || 0) + 1}
                      </div>
                      <div className="font-display text-lg uppercase mt-0.5">{l.planned_name || "Session"}</div>
                      {l.planned_type && <div className="text-[10px] font-mono-jp text-[#888] uppercase tracking-widest mt-0.5">{l.planned_type}{l.planned_distance_km ? ` · planned ${l.planned_distance_km}km` : ""}{l.planned_pace_target ? ` @ ${l.planned_pace_target}` : ""}</div>}
                    </div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">{(l.completed_at || "").slice(0, 10)}</div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <LogStat label="Feel" v={l.feel} accent="#d7ff00" />
                    <LogStat label="RPE" v={l.rpe ? `${l.rpe}/10` : null} accent="#d7ff00" />
                    <LogStat label="Actual KM" v={l.actual_distance_km ? `${l.actual_distance_km} km` : null} />
                    <LogStat label="Actual Pace" v={l.actual_pace} />
                  </div>
                  {l.actual_duration_min && (
                    <div className="mt-2 text-sm text-[#ddd]">Duration: <span className="text-white">{l.actual_duration_min} min</span></div>
                  )}

                  {l.intervals && (
                    <div className="mt-3 bg-[#070707] border border-[#222] p-3">
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] mb-1.5">INTERVAL SPLITS</div>
                      <div className="text-sm text-white whitespace-pre-wrap">{l.intervals}</div>
                    </div>
                  )}
                  {l.notes && (
                    <div className="mt-3 bg-[#070707] border border-[#d7ff00]/30 p-3">
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] mb-1.5">NOTE TO COACH</div>
                      <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{l.notes}</div>
                    </div>
                  )}
                </div>
              ))}

              {/* Manual ad-hoc workouts */}
              {manualWorkouts.map(m => (
                <div key={m.id} className="bg-[#0a0a0a] border border-[#1f1f1f] border-l-4 border-l-[#71c4ff] p-4" data-testid={`manual-workout-${m.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#71c4ff]">AD-HOC · {(m.type || "easy").toUpperCase()}</div>
                      <div className="font-display text-lg uppercase mt-0.5">{m.name}</div>
                    </div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">{m.workout_date}</div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <LogStat label="Distance" v={m.distance_km ? `${m.distance_km} km` : null} accent="#71c4ff" />
                    <LogStat label="Duration" v={m.duration_min ? `${m.duration_min} min` : null} />
                    <LogStat label="Pace" v={m.pace} />
                    <LogStat label="Feel · RPE" v={m.feel ? `${m.feel}${m.rpe ? ` · ${m.rpe}/10` : ""}` : null} accent="#d7ff00" />
                  </div>

                  {m.intervals && (
                    <div className="mt-3 bg-[#070707] border border-[#222] p-3">
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#71c4ff] mb-1.5">INTERVALS</div>
                      <div className="text-sm text-white whitespace-pre-wrap">{m.intervals}</div>
                    </div>
                  )}
                  {m.notes && (
                    <div className="mt-3 bg-[#070707] border border-[#71c4ff]/30 p-3">
                      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#71c4ff] mb-1.5">NOTE TO COACH</div>
                      <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{m.notes}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "recovery" && (
        <div data-testid="recovery-tab-content">
          {recovery.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5 text-center">
              <Heart className="w-6 h-6 text-[#444] mx-auto mb-2" />
              <div className="text-sm text-[#bbb]">No recovery logs yet.</div>
              <div className="text-xs text-[#666] mt-1">Athlete logs HRV / sleep / RHR daily on their Recovery page.</div>
            </div>
          ) : (
            <div className="bg-[#0a0a0a] border border-[#1f1f1f] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono-jp text-[10px] uppercase tracking-widest text-[#888] border-b border-[#1f1f1f]">
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">HRV</th>
                    <th className="px-4 py-2.5">Sleep</th>
                    <th className="px-4 py-2.5">RHR</th>
                    <th className="px-4 py-2.5">Stress</th>
                    <th className="px-4 py-2.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {recovery.map(r => (
                    <tr key={r.id || r.date} className="border-b border-[#141414] last:border-b-0" data-testid={`recovery-row-${r.date}`}>
                      <td className="px-4 py-2.5 font-mono-jp text-xs text-[#bbb]">{(r.date || "").slice(0, 10)}</td>
                      <td className="px-4 py-2.5 text-white">{r.hrv ?? "—"}</td>
                      <td className="px-4 py-2.5 text-white">{r.sleep_hours ? `${r.sleep_hours}h` : "—"}</td>
                      <td className="px-4 py-2.5 text-white">{r.resting_hr ?? "—"}</td>
                      <td className="px-4 py-2.5 text-white">{r.stress ?? "—"}</td>
                      <td className="px-4 py-2.5 text-[#888] text-xs truncate max-w-xs">{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PanelTab({ active, onClick, icon: Icon, count, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 px-3 py-2 font-mono-jp text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${active ? "bg-[#d7ff00] text-black" : "text-[#bbb] hover:text-white"}`}>
      <Icon className="w-3 h-3" /> {children}
      <span className={`ml-0.5 ${active ? "text-black/60" : "text-[#666]"}`}>· {count}</span>
    </button>
  );
}

function KpiBox({ label, value, accent = "#fff" }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-3">
      <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">{label}</div>
      <div className="font-display text-xl mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function LogStat({ label, v, accent = "#fff" }) {
  return (
    <div>
      <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">{label}</div>
      <div className="text-sm mt-0.5" style={{ color: v ? accent : "#555" }}>{v || "—"}</div>
    </div>
  );
}

function formatPace(minPerKm) {
  if (!minPerKm) return "—";
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDuration(seconds) {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}


function OfflinePaymentControl({ uid, firstName, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pkgs, setPkgs] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const openForm = async () => {
    setOpen(true);
    if (pkgs.length === 0) {
      try {
        const { data } = await api.get("/packages");
        setPkgs(data);
        if (data.length) { setPackageId(data[0].id); setAmount(String(data[0].price ?? "")); }
      } catch { toast.error("Could not load packages"); }
    }
  };

  const onPick = (id) => {
    setPackageId(id);
    const p = pkgs.find(x => x.id === id);
    setAmount(p ? String(p.price ?? "") : "");
  };

  const submit = async () => {
    if (!packageId) { toast.error("Pick a package"); return; }
    setBusy(true);
    try {
      const body = { package_id: packageId, note: note.trim() || undefined };
      if (amount !== "") body.amount = parseFloat(amount);
      const { data } = await api.post(`/admin/athletes/${uid}/record-payment`, body);
      toast.success(`Payment recorded · ${firstName} activated${data.is_monthly ? " (renews in 30d)" : ""}`);
      setOpen(false); setNote("");
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`offline-payment-control-${uid}`}>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1">
        <DollarSign className="w-3 h-3" /> OFFLINE / BANK TRANSFER
      </div>
      {!open ? (
        <button
          onClick={openForm}
          className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-[#070707] bg-[#71c4ff] px-3 py-2 hover:bg-white"
          data-testid={`record-payment-${uid}`}
        >
          Record offline payment
        </button>
      ) : (
        <div className="mt-2 space-y-2">
          <div>
            <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">Package</label>
            <select
              value={packageId}
              onChange={e => onPick(e.target.value)}
              className="w-full mt-1 bg-[#0e0e0e] border border-[#222] focus:border-[#71c4ff] outline-none px-2 py-2 text-white text-sm"
              data-testid={`record-payment-package-${uid}`}
            >
              {pkgs.map(p => <option key={p.id} value={p.id}>{p.name} · {p.currency} {p.price}{p.period || ""}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">Amount</label>
              <input
                value={amount} onChange={e => setAmount(e.target.value)} type="number" inputMode="decimal"
                className="w-full mt-1 bg-[#0e0e0e] border border-[#222] focus:border-[#71c4ff] outline-none px-2 py-2 text-white text-sm"
                data-testid={`record-payment-amount-${uid}`}
              />
            </div>
          </div>
          <div>
            <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">Note / reference</label>
            <input
              value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Bank transfer 8 Jun"
              className="w-full mt-1 bg-[#0e0e0e] border border-[#222] focus:border-[#71c4ff] outline-none px-2 py-2 text-white text-sm"
              data-testid={`record-payment-note-${uid}`}
            />
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <button onClick={() => submit(false)} disabled={busy} className="w-full bg-[#111] border border-[#71c4ff] text-[#71c4ff] font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:bg-[#71c4ff]/10 disabled:opacity-50" data-testid={`record-payment-record-only-${uid}`}>{busy ? "…" : "Record only · don't change program"}</button>
            <button onClick={() => submit(true)} disabled={busy} className="w-full bg-[#71c4ff] text-black font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:bg-white disabled:opacity-50" data-testid={`record-payment-confirm-${uid}`}>{busy ? "…" : "Record & activate"}</button>
            <button onClick={() => setOpen(false)} disabled={busy} className="w-full bg-transparent border border-[#333] text-[#888] font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:border-[#555] disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


function CoachingControl({ uid, firstName, hasCoaching, startDate, endDate, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(startDate || "");
  const [end, setEnd] = useState(endDate || "");

  useEffect(() => { setStart(startDate || ""); setEnd(endDate || ""); }, [startDate, endDate]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const dateActive = endDate ? (endDate >= todayISO && (!startDate || startDate <= todayISO)) : false;
  const daysRemaining = endDate ? Math.max(0, Math.round((new Date(endDate + "T00:00:00") - new Date(todayISO + "T00:00:00")) / 86400000)) : null;
  const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/athletes/${uid}/grant-coaching`, { days: 30 });
      toast.success(`Coaching granted to ${firstName} · 30 days`);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    }
    setBusy(false);
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/athletes/${uid}/revoke-coaching`);
      toast.success(`Coaching revoked for ${firstName}`);
      setConfirmRevoke(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
    setBusy(false);
  };

  const saveAccess = async () => {
    if (!end) { toast.error("Set an end date to grant access"); return; }
    if (start && start > end) { toast.error("Start date must be on or before end date"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/athletes/${uid}/coaching-access`, { start_date: start || null, end_date: end });
      toast.success(`Coaching access saved for ${firstName}`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    setBusy(false);
  };

  const clearAccess = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/athletes/${uid}/coaching-access`, { start_date: null, end_date: null });
      toast.success(`Date-based access cleared for ${firstName}`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      {/* ===== Date-based coaching access (primary control) ===== */}
      <div className={`bg-[#0a0a0a] border p-3 ${dateActive ? "border-[#d7ff00]/40" : "border-[#1f1f1f]"}`} data-testid={`coaching-access-${uid}`}>
        <div className="flex items-center justify-between">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1">
            <CalendarIcon className="w-3 h-3" /> COACHING ACCESS
          </div>
          <span className={`font-mono-jp text-[9px] uppercase tracking-widest px-2 py-0.5 ${dateActive ? "bg-[#d7ff00] text-black" : "bg-[#1f1f1f] text-[#888]"}`} data-testid={`coaching-status-${uid}`}>
            {dateActive ? "ACTIVE" : "INACTIVE"}
          </span>
        </div>

        {!editing ? (
          <>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <div className="bg-[#111] border border-[#222] py-2">
                <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">Start</div>
                <div className="text-xs text-white mt-0.5" data-testid={`coaching-start-${uid}`}>{fmt(startDate)}</div>
              </div>
              <div className="bg-[#111] border border-[#222] py-2">
                <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">End</div>
                <div className="text-xs text-white mt-0.5" data-testid={`coaching-end-${uid}`}>{fmt(endDate)}</div>
              </div>
              <div className="bg-[#111] border border-[#222] py-2">
                <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">Days left</div>
                <div className={`text-xs mt-0.5 ${dateActive ? "text-[#d7ff00]" : "text-[#888]"}`} data-testid={`coaching-days-${uid}`}>{daysRemaining != null ? daysRemaining : "—"}</div>
              </div>
            </div>
            <button
              onClick={() => setEditing(true)}
              className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-3 py-2 hover:bg-[#d7ff00]/10"
              data-testid={`edit-coaching-access-${uid}`}
            >
              Edit access
            </button>
          </>
        ) : (
          <div className="mt-2 space-y-2">
            <div>
              <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">Start date</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className="mt-1 w-full bg-[#111] border border-[#222] focus:border-[#d7ff00] outline-none px-2 py-1.5 text-sm text-white" data-testid={`coaching-start-input-${uid}`} />
            </div>
            <div>
              <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">End date</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="mt-1 w-full bg-[#111] border border-[#222] focus:border-[#d7ff00] outline-none px-2 py-1.5 text-sm text-white" data-testid={`coaching-end-input-${uid}`} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); setStart(startDate || ""); setEnd(endDate || ""); }} disabled={busy} className="flex-1 bg-[#111] border border-[#333] text-white font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:border-[#d7ff00] disabled:opacity-50">Cancel</button>
              <button onClick={saveAccess} disabled={busy} className="flex-1 bg-[#d7ff00] text-black font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:bg-white disabled:opacity-50" data-testid={`save-coaching-access-${uid}`}>{busy ? "…" : "Save access"}</button>
            </div>
            {endDate && (
              <button onClick={clearAccess} disabled={busy} className="w-full font-mono-jp text-[9px] uppercase tracking-widest text-[#ff6b35] border border-[#ff6b35]/30 py-1.5 hover:bg-[#ff6b35]/10 disabled:opacity-50" data-testid={`clear-coaching-access-${uid}`}>Clear dates</button>
            )}
          </div>
        )}
      </div>

      {/* ===== Legacy subscription grant/revoke (still available) ===== */}
      {hasCoaching ? (
        <div className="bg-[#0a0a0a] border border-[#d7ff00]/40 p-3" data-testid={`coaching-control-${uid}`}>
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> COACHING ACTIVE
          </div>
          {!confirmRevoke ? (
            <button
              onClick={() => setConfirmRevoke(true)}
              className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-[#ff6b35] border border-[#ff6b35]/40 px-3 py-2 hover:bg-[#ff6b35]/10"
              data-testid={`revoke-coaching-${uid}`}
            >
              Revoke subscription access
            </button>
          ) : (
            <div className="mt-2 space-y-2">
              <div className="text-xs text-[#bbb]">Cancels active coaching subscriptions for {firstName} immediately. Program & history remain. (Does not clear date-based access above.)</div>
              <div className="flex gap-2">
                <button onClick={() => setConfirmRevoke(false)} disabled={busy} className="flex-1 bg-[#111] border border-[#333] text-white font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:border-[#d7ff00] disabled:opacity-50">Keep</button>
                <button onClick={revoke} disabled={busy} className="flex-1 bg-[#ff6b35] text-black font-mono-jp text-[10px] uppercase tracking-widest py-2 hover:bg-white disabled:opacity-50" data-testid={`revoke-confirm-${uid}`}>{busy ? "…" : "Revoke now"}</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`coaching-control-${uid}`}>
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> NO SUBSCRIPTION
          </div>
          <button
            onClick={grant}
            disabled={busy}
            className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-[#070707] bg-[#d7ff00] px-3 py-2 hover:bg-white disabled:opacity-50"
            data-testid={`grant-coaching-${uid}`}
          >
            {busy ? "Granting…" : "Grant 30-day sub"}
          </button>
        </div>
      )}
    </div>
  );
}


function ResetAssignmentControl({ uid, firstName, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [diag, setDiag] = useState(null);
  const [logsDump, setLogsDump] = useState(null);

  const runDiag = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/athletes/${uid}/dashboard-debug`);
      setDiag(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostic failed");
    }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/athletes/${uid}/reset-assignment`);
      toast.success(`Reset → ${data.new_program_name} (${data.session_count} sessions)`);
      setConfirm(false);
      setDiag(null);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
    setBusy(false);
  };

  const removeProgram = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/athletes/${uid}/assignment`);
      toast.success(data.removed ? `Removed · ${data.removed_program_name || "program"}` : (data.message || "No program assigned"));
      setConfirmRemove(false);
      setDiag(null);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
    setBusy(false);
  };

  const recalc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/athletes/${uid}/recalc-mileage`);
      toast.success(`Recalculated · ${data.fixed} fixed / ${data.scanned} scanned`);
      if (data.hint) toast.info(data.hint, { duration: 8000 });
      setLogsDump(null);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recalc failed");
    }
    setBusy(false);
  };

  const dumpLogs = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/athletes/${uid}/logs-debug`);
      setLogsDump(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logs dump failed");
    }
    setBusy(false);
  };

  const previewDash = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/athletes/${uid}/dashboard-preview`);
      const summary = `Weekly: ${data.weekly_mileage} km · has_program: ${data.has_program} · today: ${data.today_session?.name || "—"}`;
      toast.success(summary, { duration: 10000 });
      // also dump in a div via logsDump for visibility
      setLogsDump({
        log_count: "(see dashboard preview below)",
        athlete: { name: "Dashboard API preview" },
        total_actual_distance_km: data.weekly_mileage,
        total_with_fallback_km: data.monthly_mileage,
        current_program_name: data.has_program ? "has_program=TRUE" : "has_program=FALSE",
        current_program_id: `today_session=${data.today_session ? data.today_session.name : "NULL"}`,
        logs: [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
    setBusy(false);
  };

  const fixProgramDistances = async () => {
    if (!diag?.program_summary?.id) {
      toast.error("Run Diagnose first to identify the program");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/programs/${diag.program_summary.id}/auto-fill-distances`);
      toast.success(`Filled ${data.fixed}/${data.total} session distances`);
      // re-diagnose
      const fresh = await api.get(`/admin/athletes/${uid}/dashboard-debug`);
      setDiag(fresh.data);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fix failed");
    }
    setBusy(false);
  };

  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`reset-control-${uid}`}>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1">
        <Activity className="w-3 h-3" /> TRAINING PROGRAM
      </div>
      {diag && (
        <div className="mt-2 text-xs text-[#bbb] space-y-1 bg-[#070707] border border-[#222] p-2 max-h-48 overflow-y-auto">
          <div>Program: <span className="text-white">{diag.program_summary?.name || "—"}</span></div>
          <div>Sessions: <span className="text-white">{diag.program_summary?.total_sessions ?? 0}</span> · Weeks: <span className="text-white">{diag.program_summary?.weeks ?? "—"}</span></div>
          <div>This week sessions: <span className="text-white">{diag.current_week_sessions?.length ?? 0}</span></div>
          {diag.issues?.length > 0 ? (
            <div className="mt-1 text-[#ff9500]">
              {diag.issues.map((iss, i) => <div key={`${iss}-${i}`}>· {iss}</div>)}
            </div>
          ) : (
            <div className="mt-1 text-[#d7ff00]">✓ No issues detected</div>
          )}
        </div>
      )}
      {diag?.issues?.some(i => i.includes("SESSIONS_WITHOUT_PLANNED_DISTANCE")) && (
        <button
          onClick={fixProgramDistances}
          disabled={busy}
          className="mt-2 w-full font-mono-jp text-[10px] uppercase tracking-widest text-black bg-[#d7ff00] px-3 py-2 hover:bg-white disabled:opacity-50"
          data-testid={`fix-distances-${uid}`}
        >
          {busy ? "…" : "Auto-fill missing distances"}
        </button>
      )}
      {logsDump && (
        <div className="mt-2 text-[10px] text-[#bbb] bg-[#070707] border border-[#222] p-2 max-h-72 overflow-y-auto font-mono-jp" data-testid={`logs-dump-${uid}`}>
          <div className="text-[#d7ff00]">{logsDump.log_count} LOGS · ACTUAL TOTAL {logsDump.total_actual_distance_km} KM · WITH FALLBACK {logsDump.total_with_fallback_km} KM</div>
          <div className="mt-1 text-[#666]">Program: {logsDump.current_program_name} ({logsDump.current_program_id})</div>
          <table className="w-full mt-2 text-[9px]">
            <thead className="text-[#666] text-left">
              <tr>
                <th>DATE</th><th>W</th><th>D</th><th>ACT</th><th>PLAN</th><th>USED</th><th>MATCH</th>
              </tr>
            </thead>
            <tbody>
              {logsDump.logs.map((l, i) => (
                <tr key={i} className={l.effective_km_for_dashboard ? "" : "text-[#ff9500]"}>
                  <td>{(l.completed_at || "").slice(0, 10)}</td>
                  <td>{l.week_index}</td>
                  <td>{l.day_index}</td>
                  <td>{l.log_actual_distance_km ?? "—"}</td>
                  <td>{l.planned_distance_km ?? "—"}</td>
                  <td className={l.effective_km_for_dashboard ? "text-[#d7ff00]" : "text-[#ff9500]"}>{l.effective_km_for_dashboard}</td>
                  <td>{l.matches_current_program ? "✓" : "✗"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          onClick={runDiag}
          disabled={busy}
          className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] border border-[#333] px-3 py-2 hover:border-[#d7ff00] disabled:opacity-50"
          data-testid={`diagnose-${uid}`}
        >
          {busy && !confirm ? "…" : "Diagnose"}
        </button>
        <button
          onClick={recalc}
          disabled={busy}
          className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-3 py-2 hover:bg-[#d7ff00]/10 disabled:opacity-50"
          data-testid={`recalc-${uid}`}
          title="Back-fill missing distance on completed sessions using planned km"
        >
          Recalc mileage
        </button>
        <button
          onClick={dumpLogs}
          disabled={busy}
          className="font-mono-jp text-[10px] uppercase tracking-widest text-[#71c4ff] border border-[#71c4ff]/40 px-3 py-2 hover:bg-[#71c4ff]/10 disabled:opacity-50"
          data-testid={`dump-logs-${uid}`}
          title="Show every completed session + matched planned km"
        >
          Show session logs
        </button>
        <button
          onClick={previewDash}
          disabled={busy}
          className="font-mono-jp text-[10px] uppercase tracking-widest text-[#c084fc] border border-[#c084fc]/40 px-3 py-2 hover:bg-[#c084fc]/10 disabled:opacity-50"
          data-testid={`preview-dashboard-${uid}`}
          title="Show the exact /api/dashboard response athlete sees"
        >
          Preview athlete dashboard
        </button>
        {!confirm ? (
          <button
            onClick={() => setConfirm(true)}
            className="col-span-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#ff9500] border border-[#ff9500]/40 px-3 py-2 hover:bg-[#ff9500]/10"
            data-testid={`reset-${uid}`}
          >
            Reset assignment
          </button>
        ) : (
          <button
            onClick={reset}
            disabled={busy}
            className="col-span-2 font-mono-jp text-[10px] uppercase tracking-widest text-black bg-[#ff9500] px-3 py-2 hover:bg-white disabled:opacity-50"
            data-testid={`reset-confirm-${uid}`}
          >
            {busy ? "…" : "Confirm reset"}
          </button>
        )}
        {!confirmRemove ? (
          <button
            onClick={() => setConfirmRemove(true)}
            className="col-span-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#ff5470] border border-[#ff5470]/40 px-3 py-2 hover:bg-[#ff5470]/10"
            data-testid={`remove-program-${uid}`}
          >
            Remove program
          </button>
        ) : (
          <button
            onClick={removeProgram}
            disabled={busy}
            className="col-span-2 font-mono-jp text-[10px] uppercase tracking-widest text-black bg-[#ff5470] px-3 py-2 hover:bg-white disabled:opacity-50"
            data-testid={`remove-program-confirm-${uid}`}
          >
            {busy ? "…" : "Confirm remove · no program"}
          </button>
        )}
      </div>
      {confirm && (
        <div className="mt-2 text-[10px] text-[#666] font-mono-jp uppercase tracking-widest">Drops current assignment + re-creates fresh week 1</div>
      )}
      {confirmRemove && (
        <div className="mt-2 text-[10px] text-[#666] font-mono-jp uppercase tracking-widest">Unassigns the program — athlete shows &ldquo;preparing your program&rdquo; until you assign a new one</div>
      )}
    </div>
  );
}
